import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';
import { getPublisherIdByPortalId } from 'utils/portalUtils';
import { LanguageDataParams } from 'views/Localization/components/types';

import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import {
  EApiRoutes,
  fetchConfig,
  getApiRouteOrMock,
  getCustomHeader
} from './api.utils';

const useLocalization = () => {
  const axios = useAxios();
  const { publisherId, portalId } = useParams();

  const getAddedLanguagesList = useQuery({
    ...fetchConfig.list,
    queryKey: [EQueryKeys.LOCALIZATION_ADDED_LANGUAGES_LIST],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/languages`,
        {},
        getCustomHeader(publisherId, portalId)
      );
    }
  });

  const addNewLanguage = useMutation(
    async (newLanguagesData: LanguageDataParams[]) => {
      const response = await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/languages`,
        { languages: newLanguagesData },
        getCustomHeader(publisherId, portalId)
      );
      return response.data;
    },
    {}
  );

  const editLanguage = useMutation(
    async (newLanguagesData: LanguageDataParams[]) => {
      return await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/languages`,
        { languages: newLanguagesData },
        getCustomHeader(publisherId, portalId)
      );
    },
    {}
  );

  const deleteLanguage = useMutation(
    async (languagesData: LanguageDataParams[]) => {
      return await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/languages`,
        { languages: languagesData },
        getCustomHeader(publisherId, portalId)
      );
    },
    {}
  );

  const importStaticTranslationsCsvFile = useMutation(
    async (newFile: FormData) => {
      const response = await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/files`,
        newFile,
        getCustomHeader(publisherId, portalId)
      );
      return response.data;
    }
  );

  const importDynamicTranslationsCsvFile = useMutation(
    async (newFile: FormData) => {
      const response = await axios.post(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/dynamic-files`,
        newFile,
        getCustomHeader(publisherId, portalId)
      );
      return response.data;
    }
  );

  const getStaticTranslationsCsvFile = useQuery({
    ...fetchConfig.single,
    queryKey: [EQueryKeys.LOCALIZATION_STATIC_TRANSLATIONS_FILE],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/files`,
        {},
        getCustomHeader(publisherId, portalId)
      );
    },
    enabled: false,
    retry: false
  });

  const getDynamicTranslationsCsvFile = useQuery({
    ...fetchConfig.single,
    queryKey: [EQueryKeys.LOCALIZATION_DYNAMIC_TRANSLATIONS_FILE],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/dynamic-files`,
        {},
        getCustomHeader(publisherId, portalId)
      );
    },
    enabled: false,
    retry: false
  });

  const getStaticTranslationsCsvLastUpdateDate = useQuery({
    ...fetchConfig.single,
    queryKey: [EQueryKeys.LOCALIZATION_STATIC_TRANSLATIONS_FILE_LAST_UPDATE],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/files/last-update`,
        {},
        getCustomHeader(publisherId, portalId)
      );
    }
  });

  const getDynamicTranslationsCsvLastUpdateDate = useQuery({
    ...fetchConfig.single,
    queryKey: [EQueryKeys.LOCALIZATION_DYNAMIC_TRANSLATIONS_FILE_LAST_UPDATE],
    queryFn: async () => {
      return await axios.get(
        `${getApiRouteOrMock(EApiRoutes.LOCALIZATION_TRANSLATIONS)}/dynamic-files/last-update`,
        {},
        getCustomHeader(publisherId, portalId)
      );
    }
  });

  return {
    getAddedLanguagesList,
    addNewLanguage,
    deleteLanguage,
    importStaticTranslationsCsvFile,
    importDynamicTranslationsCsvFile,
    getStaticTranslationsCsvFile,
    getDynamicTranslationsCsvFile,
    getStaticTranslationsCsvLastUpdateDate,
    getDynamicTranslationsCsvLastUpdateDate,
    editLanguage
  };
};

export default useLocalization;
